import { makeAutoObservable, reaction } from 'mobx';
import { component } from 'tsdi';

import {
    type AllowedCountries,
    type LanguageCode,
    type UserLocale
} from '../utils/locale';

export interface ServerError<T = { error: string; error_description: string }>
    extends Error {
    response: Response;
    body: T;
}

@component
export class I18nStore {
    public language?: LanguageCode = undefined;
    public country?: AllowedCountries = undefined;

    private version = 0;

    public translate: (
        key: string | null | undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...params: any[]
    ) => string = () => '';

    constructor() {
        makeAutoObservable(this);

        reaction(
            () => this.language,
            (lang) => {
                if (lang) {
                    void this.loadTranslationBundle(lang);
                }
            },
            { fireImmediately: true }
        );
    }

    public setLocale = ({
        lang = this.language,
        countryCode = this.country
    }: Partial<UserLocale>) => {
        this.country = countryCode;
        this.language = lang;
        if (lang) {
            document.documentElement.lang = lang;
        }
    };

    public hasTranslation: (
        key: string | null | undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...params: any[]
    ) => boolean = () => false;

    private async loadTranslationBundle(localeParam: string): Promise<void> {
        if (typeof localeParam !== 'string') {
            return;
        }
        const loc = localeParam.replace(/"/g, '') || this.language;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const source: { [key: string]: string } = await (
            await fetch(
                `https://intl.sportalliance.com/ml-try-magicline-webclient/${
                    loc || 'en'
                }.json`
            )
        ).json();

        const translate = (
            key: string | null | undefined,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params: any
        ): string => {
            if (!key) {
                return '';
            }
            const translated = source[key];
            if (!translated) {
                return key.toString();
            }

            return replacePlaceHolder(translated, params);
        };

        this.translate = this.wrap(translate);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        (window as any).translate = translate;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        ((window as any).ml = (window as any).ml || {})['i18n-store'] = this;

        this.hasTranslation = (key) => Boolean(key && source[key]);
        this.version++;

    }

    private wrap(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fn: (key: string | null | undefined, ...params: any[]) => string
    ): (
        key: string | null | undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...params: any[]
    ) => string {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const convertParams = (params: any[]) => {
            // note: allowed values for params are
            // [ 'value1', 'value2', 'value3' ] and
            // [ { '0': 'value1', '1': 'value2', '2': 'value3' } ]
            // params as string-array will be transformed to the latter syntax.
            if (params[0] !== undefined && typeof params[0] !== 'object') {
                // eslint-disable-next-line no-param-reassign
                params = [
                    params.reduce(
                        (
                            memo: { [key: string]: string | number },
                            param: string | number,
                            i: number
                        ) => {
                            memo[`${i}`] = param;
                            return memo;
                        },
                        {}
                    )
                ];
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return params;
        };

        return (key, ...params) => {
            if (key === null || key === undefined) {
                return '';
            }

            try {
                // eslint-disable-next-line
                const value = fn(key, ...convertParams(params));
                if (key === value) {
                    console.warn(
                        `%ci18n missing: %c${value}`,
                        'color:#94999d',
                        'color:#000000'
                    );
                }
                return value;
            } catch (e) {
                const err = e as ServerError;
                console.warn(err.message);
                return '';
            }
        };
    }

    public __ = (
        key: string | null | undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...params: any[]
    ): string =>
        // eslint-disable-next-line
        this.translate(key, ...params);

    public get translationsReady(): boolean {
        return this.version > 0;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function replacePlaceHolder(translated: string, params: any): string {
    if (!params) {
        return translated;
    }
    return (
        translated
            .replace(/\${/g, '___dollar___bracket___')
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
            .replace(/{\w}/g, (v) => params[v[1]])
            .replace(/___dollar___bracket___/g, '${')
            .replace(
                // MLWC-9906 workaround for broken check in convert to icu process
                /(%\d\$s)/g,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
                (v) => params[`${parseInt(v.replace('%', ''), 10) - 1}`]
            )
    );
}
